import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProjectPreOrderLayoutWrapper from "../../components/ProjectPreOrderLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSearch } from "react-icons/fa";

const API = "https://nissi-india.com/api/projectClearValues";

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmt2 = (v) =>
  toNum(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const isDDMMYYYY = (s) => {
  if (!s) return false;
  const m = String(s).trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return false;
  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);
  if (mm < 1 || mm > 12) return false;
  if (dd < 1 || dd > 31) return false;
  const dt = new Date(yyyy, mm - 1, dd);
  return (
    dt.getFullYear() === yyyy &&
    dt.getMonth() === mm - 1 &&
    dt.getDate() === dd
  );
};

// convert "dd/MM/yyyy" -> "yyyy-MM-dd" for <input type="date">
const ddmmyyyyToISO = (s) => {
  if (!isDDMMYYYY(s)) return "";
  const [dd, mm, yyyy] = s.split("/");
  return `${yyyy}-${mm}-${dd}`;
};

// convert "yyyy-MM-dd" -> "dd/MM/yyyy"
const isoToDDMMYYYY = (iso) => {
  if (!iso) return "";
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
};

const EMDESDAndFSD = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // localStorage helper
  const getLS = (key) => localStorage.getItem(key) || "";

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  // common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // dropdown projects
  const [projects, setProjects] = useState([]);

  // top + bottom selected projects
  const [topProjectId, setTopProjectId] = useState("Select");
  const [filterProjectId, setFilterProjectId] = useState("Select");

  // top grid rows (pending or edit-loaded)
  const [topRows, setTopRows] = useState([]);
  // list rows (bottom grid)
  const [listRows, setListRows] = useState([]);

  // edit mode like Session["editflg"]
  const [editMode, setEditMode] = useState(false);

  const [loadingTop, setLoadingTop] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);

  const totalTop = useMemo(
    () => topRows.reduce((s, r) => s + toNum(r.amount), 0),
    [topRows]
  );
  const totalList = useMemo(
    () => listRows.reduce((s, r) => s + toNum(r.amount), 0),
    [listRows]
  );

  // ===== API calls =====
  const loadProjects = async () => {
    if (!branchId) {
      toast.error("branchId not found in localStorage");
      return;
    }

    try {
      const res = await axios.get(`${API}/projects`, {
        params: { branchid: branchId },
        headers: commonHeaders,
      });

      if (res.data?.success) {
        setProjects(res.data?.list || []);
      } else {
        toast.error(res.data?.message || "Failed to load projects");
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load projects");
    }
  };

  const loadPendingTop = async (pid) => {
    setLoadingTop(true);
    try {
      const res = await axios.get(`${API}/pending`, {
        params: { projectId: pid },
        headers: commonHeaders,
      });

      if (res.data?.success) {
        const rows = (res.data?.rows || []).map((r, idx) => ({
          id: `${pid}-${idx}`,
          valueName: r.valueName || r.ValueName || "EMD",
          amount: toNum(r.amount ?? r.Amount),
          receivedDate: r.receivedDate || r.fld_receiveddatenew || "",
          bankName: r.bankName || r.fld_bankname || "",
          branchName: r.branchName || r.fld_branchname || "",
          chequeNo: r.chequeNo || r.fld_chequeno || "",
          emdAlertDate: r.emdAlertDate || r.fld_emdalertdate || "",
          alert: r.alert || r.fld_alert || "",
          checked: false,
        }));
        setTopRows(rows);
      } else {
        toast.error(res.data?.message || "Failed to load pending rows");
        setTopRows([]);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load pending rows");
      setTopRows([]);
    } finally {
      setLoadingTop(false);
    }
  };

  const loadList = async (pid) => {
    setLoadingList(true);
    try {
      const res = await axios.get(`${API}/list`, {
        params: { projectId: pid },
        headers: commonHeaders,
      });

      if (res.data?.success) {
        const rows = (res.data?.rows || []).map((r, idx) => ({
          id: `${pid}-list-${idx}`,
          valueName: r.valueName || r.ValueName || "EMD",
          amount: toNum(r.amount ?? r.Amount),
          receivedDate: r.receivedDate || r.fld_receiveddatenew || "",
          bankName: r.bankName || r.fld_bankname || "",
          branchName: r.branchName || r.fld_branchname || "",
          chequeNo: r.chequeNo || r.fld_chequeno || "",
          emdAlertDate: r.emdAlertDate || r.fld_emdalertdate || "",
          alert: r.alert || r.fld_alert || "",
        }));
        setListRows(rows);
      } else {
        toast.error(res.data?.message || "Failed to load list");
        setListRows([]);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load list");
      setListRows([]);
    } finally {
      setLoadingList(false);
    }
  };

  // ===== UI handlers =====
  const handleTopSubmit = async () => {
    if (topProjectId === "Select") {
      toast.error("Please select Project Details");
      return;
    }
    setEditMode(false);
    await loadPendingTop(Number(topProjectId));
  };

  const handleSearch = async () => {
    if (filterProjectId === "Select") {
      toast.error("pls select Project");
      return;
    }
    await loadList(Number(filterProjectId));
  };

  const handleExcel = () => {
    if (filterProjectId === "Select") {
      toast.error("pls select Project");
      return;
    }

    const url = new URL(`${API}/excel`);
    url.searchParams.append("projectId", filterProjectId);
    url.searchParams.append("finYearId", finYearId);
    url.searchParams.append("finYear", finYear);
    url.searchParams.append("branchId", branchId);
    url.searchParams.append("branchType", branchType);
    url.searchParams.append("userId", userId);

    window.open(url.toString(), "_blank");
  };

  const handlePrint = () => {
    if (filterProjectId === "Select") {
      toast.error("pls select Project");
      return;
    }

    const url = new URL(`${API}/print`);
    url.searchParams.append("projectId", filterProjectId);
    url.searchParams.append("finYearId", finYearId);
    url.searchParams.append("finYear", finYear);
    url.searchParams.append("branchId", branchId);
    url.searchParams.append("branchType", branchType);
    url.searchParams.append("userId", userId);

    window.open(url.toString(), "_blank", "width=900,height=900,scrollbars=yes");
  };

  const handleEditFromList = async (pid) => {
    setTopProjectId(String(pid));
    setEditMode(true);

    setLoadingTop(true);
    try {
      const res = await axios.get(`${API}/list`, {
        params: { projectId: pid },
        headers: commonHeaders,
      });

      if (res.data?.success) {
        const rows = (res.data?.rows || []).map((r, idx) => ({
          id: `${pid}-edit-${idx}`,
          valueName: r.valueName || r.ValueName || "EMD",
          amount: toNum(r.amount ?? r.Amount),
          receivedDate: r.receivedDate || r.fld_receiveddatenew || "",
          bankName: r.bankName || r.fld_bankname || "",
          branchName: r.branchName || r.fld_branchname || "",
          chequeNo: r.chequeNo || r.fld_chequeno || "",
          emdAlertDate: r.emdAlertDate || r.fld_emdalertdate || "",
          alert: r.alert || r.fld_alert || "",
          checked: true,
        }));
        setTopRows(rows);
      } else {
        toast.error(res.data?.message || "Failed to load edit data");
        setTopRows([]);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load edit data");
      setTopRows([]);
    } finally {
      setLoadingTop(false);
    }
  };

  const setRow = (rowId, patch) => {
    setTopRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, ...patch } : r))
    );
  };

  const toggleAll = (checked) => {
    setTopRows((prev) => prev.map((r) => ({ ...r, checked })));
  };

  const validateTopRowsForSave = () => {
    if (topProjectId === "Select") return "Please select Project Details";
    if (topRows.length === 0) return "No Records Found";

    if (editMode) {
      const r = topRows[0];
      if (!isDDMMYYYY(r.receivedDate)) return "Received Date must be dd/MM/yyyy";
      if (!r.bankName?.trim()) return "Bank is required";
      if (!r.branchName?.trim()) return "Branch is required";
      if (!r.chequeNo?.trim()) return "Cheque/DD No is required";
      if (!isDDMMYYYY(r.emdAlertDate)) return "SetAlertDate must be dd/MM/yyyy";
      return "";
    }

    const anyChecked = topRows.some((r) => r.checked);
    if (!anyChecked) return "Please select any one";

    for (const r of topRows.filter((x) => x.checked)) {
      if (!isDDMMYYYY(r.receivedDate)) return "Received Date must be dd/MM/yyyy";
      if (!r.bankName?.trim()) return "Bank is required";
      if (!r.branchName?.trim()) return "Branch is required";
      if (!r.chequeNo?.trim()) return "Cheque/DD No is required";
    }
    return "";
  };

  const handleGridSubmit = async () => {
    if (saving) return;

    const msg = validateTopRowsForSave();
    if (msg) {
      toast.error(msg);
      return;
    }

    if (!editMode) {
      const ok = window.confirm("Are you sure to Submit");
      if (!ok) return;
    }

    setSaving(true);
    try {
      const pid = Number(topProjectId);

      if (editMode) {
        const r = topRows[0];
        const payload = {
          projectId: pid,
          receivedDate: r.receivedDate,
          bankName: r.bankName,
          branchName: r.branchName,
          chequeNo: r.chequeNo,
          emdAlertDate: r.emdAlertDate,
        };

        const res = await axios.post(`${API}/update`, payload, {
          headers: commonHeaders,
        });

        if (res.data?.success) {
          toast.success(res.data?.message || "Updated Successfully");
          await loadList(pid);
          await handleEditFromList(pid);
        } else {
          toast.error(res.data?.message || "Update failed");
        }
      } else {
        const selected = topRows.filter((r) => r.checked);
        const payload = {
          projectId: pid,
          rows: selected.map((r) => ({
            valueName: r.valueName,
            amount: r.amount,
            receivedDate: r.receivedDate,
            bankName: r.bankName,
            branchName: r.branchName,
            chequeNo: r.chequeNo,
          })),
        };

        const res = await axios.post(`${API}/save`, payload, {
          headers: commonHeaders,
        });

        if (res.data?.success) {
          toast.success(res.data?.message || "Saved Successfully");
          await loadPendingTop(pid);
          if (filterProjectId === String(pid)) {
            await loadList(pid);
          }
        } else {
          toast.error(res.data?.message || "Save failed");
        }
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Server error");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  return (
    <ProjectPreOrderLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMD ESD & FSD</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            {editMode ? (
              <span className="text-xs px-3 py-1 rounded-full bg-yellow-100 text-yellow-800">
                Edit Mode
              </span>
            ) : null}
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Project
              </label>

              <select
                value={topProjectId}
                onChange={(e) => setTopProjectId(e.target.value)}
                className={`w-96 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                <option value="Select">Select</option>
                {projects.map((p) => (
                  <option key={p.projectId} value={p.projectId}>
                    {p.projectName}
                  </option>
                ))}
              </select>

              <button
                onClick={handleTopSubmit}
                disabled={loadingTop}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? `${buttonClass} ${hoverClass} text-white`
                    : `${buttonClass} ${hoverClass} text-white`
                } ${loadingTop ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                {loadingTop ? "Loading..." : "Submit"}
              </button>
            </div>
          </div>

          <div
            className={`rounded-xl shadow-sm border overflow-hidden mb-8 ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="w-14 px-4 py-4 text-center font-semibold border-r border-white/20">S.No</th>
                    <th className="w-20 px-4 py-4 text-center font-semibold border-r border-white/20">Name</th>
                    <th className="w-24 px-4 py-4 text-center font-semibold border-r border-white/20">Amount</th>
                    <th className="w-32 px-4 py-4 text-center font-semibold border-r border-white/20">Received Date</th>
                    <th className="w-32 px-4 py-4 text-center font-semibold border-r border-white/20">Bank</th>
                    <th className="w-32 px-4 py-4 text-center font-semibold border-r border-white/20">Branch</th>
                    <th className="w-36 px-4 py-4 text-center font-semibold border-r border-white/20">Cheque/DD No</th>
                    <th className="w-32 px-4 py-4 text-center font-semibold border-r border-white/20">SetAlertDate</th>
                    <th className="w-20 px-4 py-4 text-center font-semibold border-r border-white/20">Alert</th>
                    <th className="w-20 px-4 py-4 text-center font-semibold">
                      {!editMode ? (
                        <div className="flex items-center justify-center gap-2">
                          <input
                            type="checkbox"
                            onChange={(e) => toggleAll(e.target.checked)}
                            checked={topRows.length > 0 && topRows.every((r) => r.checked)}
                          />
                          <span className="text-xs">All</span>
                        </div>
                      ) : (
                        <span className="text-xs">Select</span>
                      )}
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {topRows.length === 0 ? (
                    <tr className={isDark ? "bg-gray-800" : "bg-gray-50"}>
                      <td
                        colSpan={10}
                        className={`text-center py-6 text-sm ${
                          isDark ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {loadingTop ? "Loading..." : "No Records Found"}
                      </td>
                    </tr>
                  ) : (
                    <>
                      {topRows.map((r, idx) => (
                        <tr
                          key={r.id}
                          className={`transition-colors duration-200 ${
                            isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"
                          }`}
                        >
                          <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                            {idx + 1}
                          </td>

                          <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                            {r.valueName}
                          </td>

                          <td className={`px-4 py-3 text-right border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                            {fmt2(r.amount)}
                          </td>

                          <td className={`px-4 py-3 text-center border-r min-w-0 ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                            <input
                              type="date"
                              value={ddmmyyyyToISO(r.receivedDate)}
                              onChange={(e) =>
                                setRow(r.id, {
                                  receivedDate: isoToDDMMYYYY(e.target.value),
                                })
                              }
                              className={`w-full px-2 py-1 border rounded ${
                                isDark
                                  ? "bg-gray-700 border-gray-600 text-white"
                                  : "bg-white border-gray-300 text-gray-900"
                              }`}
                            />
                            <div className="text-[10px] opacity-70 mt-1">
                              {r.receivedDate || "dd/MM/yyyy"}
                            </div>
                          </td>

                          <td className={`px-4 py-3 text-center border-r min-w-0 ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                            <input
                              value={r.bankName}
                              onChange={(e) => setRow(r.id, { bankName: e.target.value })}
                              className={`w-full px-2 py-1 border rounded ${
                                isDark
                                  ? "bg-gray-700 border-gray-600 text-white"
                                  : "bg-white border-gray-300 text-gray-900"
                              }`}
                            />
                          </td>

                          <td className={`px-4 py-3 text-center border-r min-w-0 ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                            <input
                              value={r.branchName}
                              onChange={(e) => setRow(r.id, { branchName: e.target.value })}
                              className={`w-full px-2 py-1 border rounded ${
                                isDark
                                  ? "bg-gray-700 border-gray-600 text-white"
                                  : "bg-white border-gray-300 text-gray-900"
                              }`}
                            />
                          </td>

                          <td className={`px-4 py-3 text-center border-r min-w-0 ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                            <input
                              value={r.chequeNo}
                              onChange={(e) => setRow(r.id, { chequeNo: e.target.value })}
                              className={`w-full px-2 py-1 border rounded ${
                                isDark
                                  ? "bg-gray-700 border-gray-600 text-white"
                                  : "bg-white border-gray-300 text-gray-900"
                              }`}
                            />
                          </td>

                          <td className={`px-4 py-3 text-center border-r min-w-0 ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                            <input
                              type="date"
                              value={ddmmyyyyToISO(r.emdAlertDate)}
                              onChange={(e) =>
                                setRow(r.id, {
                                  emdAlertDate: isoToDDMMYYYY(e.target.value),
                                })
                              }
                              className={`w-full px-2 py-1 border rounded ${
                                isDark
                                  ? "bg-gray-700 border-gray-600 text-white"
                                  : "bg-white border-gray-300 text-gray-900"
                              }`}
                            />
                            <div className="text-[10px] opacity-70 mt-1">
                              {r.emdAlertDate || "dd/MM/yyyy"}
                            </div>
                          </td>

                          <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                            <span
                              className={`px-2 py-1 text-xs rounded ${
                                String(r.alert).toLowerCase() === "live"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {r.alert || "-"}
                            </span>
                          </td>

                          <td className="px-4 py-3 text-center">
                            {!editMode ? (
                              <input
                                type="checkbox"
                                checked={!!r.checked}
                                onChange={(e) =>
                                  setRow(r.id, { checked: e.target.checked })
                                }
                              />
                            ) : (
                              <span className="text-xs opacity-70">—</span>
                            )}
                          </td>
                        </tr>
                      ))}

                      <tr
                        className={`font-semibold border-t-2 ${
                          isDark ? "bg-gray-750 border-gray-600" : "bg-gray-100 border-gray-300"
                        }`}
                      >
                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}></td>
                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                          Total
                        </td>
                        <td className={`px-4 py-3 text-right border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                          {fmt2(totalTop)}
                        </td>
                        <td colSpan={5} className={`px-4 py-3 text-center ${isDark ? "text-white" : "text-gray-900"}`}></td>
                        <td colSpan={2} className="px-4 py-3 text-center">
                          <button
                            onClick={handleGridSubmit}
                            disabled={saving}
                            className={`px-5 py-2 rounded-lg font-semibold shadow hover:shadow-lg transition ${
                              isDark
                                ? `${buttonClass} ${hoverClass} text-white`
                                : `${buttonClass} ${hoverClass} text-white`
                            } ${saving ? "opacity-60 cursor-not-allowed" : ""}`}
                          >
                            {saving ? "Saving..." : "Submit"}
                          </button>
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div
            className={`p-4 rounded-lg border mb-6 ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-blue-50 border-blue-200"
            }`}
          >
            <h2 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
              FILTERATION AREA
            </h2>

            <div className="flex items-center justify-center gap-4 flex-wrap">
              <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Project
              </label>

              <select
                value={filterProjectId}
                onChange={(e) => setFilterProjectId(e.target.value)}
                className={`w-96 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                <option value="Select">Select</option>
                {projects.map((p) => (
                  <option key={p.projectId} value={p.projectId}>
                    {p.projectName}
                  </option>
                ))}
              </select>

              <button
                onClick={handleSearch}
                disabled={loadingList}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? `${buttonClass} ${hoverClass} text-white`
                    : `${buttonClass} ${hoverClass} text-white`
                } ${loadingList ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSearch />
                {loadingList ? "Searching..." : "Search"}
              </button>

              <button
                onClick={handleExcel}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-green-700 hover:bg-green-600 text-white"
                    : "bg-green-600 hover:bg-green-700 text-white"
                }`}
              >
                Excel
              </button>

              <button
                onClick={handlePrint}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                Print
              </button>
            </div>
          </div>

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">S.No</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Name</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Amount</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Received Date</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Bank</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Branch</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Cheque/DD No</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">SetAlertDate</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Alert</th>
                    <th className="px-4 py-4 text-center font-semibold">Edit</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {listRows.length === 0 ? (
                    <tr className={isDark ? "bg-gray-800" : "bg-gray-50"}>
                      <td
                        colSpan={10}
                        className={`text-center py-6 text-sm ${
                          isDark ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {loadingList ? "Loading..." : "No Records Found"}
                      </td>
                    </tr>
                  ) : (
                    <>
                      {listRows.map((r, idx) => (
                        <tr
                          key={r.id}
                          className={`transition-colors duration-200 ${
                            isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"
                          }`}
                        >
                          <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                            {idx + 1}
                          </td>
                          <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                            {r.valueName}
                          </td>
                          <td className={`px-4 py-3 text-right border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                            {fmt2(r.amount)}
                          </td>
                          <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                            {r.receivedDate}
                          </td>
                          <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                            {r.bankName}
                          </td>
                          <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                            {r.branchName}
                          </td>
                          <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                            {r.chequeNo}
                          </td>
                          <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                            {r.emdAlertDate}
                          </td>
                          <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                            <span
                              className={`px-2 py-1 text-xs rounded ${
                                String(r.alert).toLowerCase() === "live"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {r.alert}
                            </span>
                          </td>
                          <td className={`px-4 py-3 text-center ${isDark ? "text-white" : "text-gray-900"}`}>
                            <button
                              onClick={() => handleEditFromList(Number(filterProjectId))}
                              className={`text-blue-600 hover:text-blue-800 underline text-xs ${
                                isDark ? "text-blue-400 hover:text-blue-300" : ""
                              }`}
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}

                      <tr
                        className={`font-semibold border-t-2 ${
                          isDark ? "bg-gray-750 border-gray-600" : "bg-gray-100 border-gray-300"
                        }`}
                      >
                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}></td>
                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          Total
                        </td>
                        <td className={`px-4 py-3 text-right border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {fmt2(totalList)}
                        </td>
                        <td colSpan={7} className={`px-4 py-3 text-center ${isDark ? "text-white" : "text-gray-900"}`}></td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPreOrderLayoutWrapper>
  );
};

export default EMDESDAndFSD;